import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { NotebookPen } from 'lucide-react'

import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'

import useMutationUpdatePlayer from '@/modules/manage-player/api/hooks/useMutationUpdatePlayer'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import showErrorToast from '@/common/utils/showErrorToast'

type PlayerNoteColorData = {
	id: string
	phone: string
	username?: string
	note?: string
	color?: string
}

const formSchema = z.object({
	note: z.string().optional(),
	color: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'กรุณากรอกรหัสสีในรูปแบบ hex (เช่น #FF0000)')
		.optional()
		.or(z.literal('')),
})

type FormValues = z.infer<typeof formSchema>

interface PlayerNoteColorModalProps {
	player: PlayerNoteColorData
}

export const PlayerNoteColorModal = ({ player }: PlayerNoteColorModalProps) => {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			note: player?.note || '',
			color: player?.color || '',
		},
	})

	useEffect(() => {
		if (open) {
			form.reset({
				note: player?.note || '',
				color: player?.color || '',
			})
		}
	}, [open, player, form])

	const { mutate: updatePlayer, isPending } = useMutationUpdatePlayer()

	const onSubmit = (values: FormValues) => {
		try {
			updatePlayer(
				{
					id: player.id,
					payload: {
						note: values.note || undefined,
						color: values.color || undefined,
					},
				},
				{
					onSuccess: () => {
						toast.success('บันทึกข้อมูลเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error as Error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
				<NotebookPen className="h-4 w-4" />
			</Button>
			{open && (
				<Dialog open onOpenChange={setOpen}>
					<DialogContent className="sm:max-w-[500px]">
						<DialogHeader>
							<DialogTitle>
								จัดการโน้ตและสี ({player.username || player.phone})
							</DialogTitle>
						</DialogHeader>

						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="space-y-4"
							>
								<FormField
									control={form.control}
									name="note"
									render={({ field }) => (
										<FormItem>
											<FormLabel>โน้ต</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													placeholder="เพิ่มโน้ตสำหรับผู้เล่นคนนี้..."
													className="min-h-[100px] resize-none"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="color"
									render={({ field }) => (
										<FormItem>
											<FormLabel>สี</FormLabel>
											<div className="space-y-2">
												<div className="flex gap-2">
													<div className="flex items-center gap-2">
														<FormControl>
															<input
																type="color"
																value={field.value || '#ffffff'}
																onChange={(e) => field.onChange(e.target.value)}
																className="h-10 w-20 cursor-pointer rounded border"
															/>
														</FormControl>
														<FormControl>
															<Input
																{...field}
																placeholder="#FF0000"
																className="w-32"
															/>
														</FormControl>
													</div>
													<div
														className="h-10 w-16 rounded-md border"
														style={{
															backgroundColor: field.value || '#ffffff',
														}}
													/>
												</div>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => field.onChange('')}
													className="w-full"
												>
													ล้างสี
												</Button>
											</div>
											<p className="text-xs text-muted-foreground">
												เลือกสีจาก color picker หรือใส่รหัสสี hex
											</p>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex justify-end gap-4 pt-4">
									<Button
										variant="outline"
										onClick={() => setOpen(false)}
										type="button"
										disabled={isPending}
									>
										ยกเลิก
									</Button>
									<Button type="submit" variant="default" disabled={isPending}>
										{isPending ? 'กำลังบันทึก...' : 'บันทึก'}
									</Button>
								</div>
							</form>
						</Form>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
